import { useMemo } from 'react'
import { type MergedPosition, useAllEulerPositions } from './useAllEulerPositions'

interface UseEulerPositionCheckOptions {
  /** The sub-account ID to look up (from activeCard.solverConfig.subAccountId) */
  subAccountId?: number
  /** Whether Euler is the currently selected market/protocol */
  isEuler: boolean
}

interface UseEulerPositionCheckResult {
  /** The matched Euler position for the given sub-account, if any */
  activePosition: MergedPosition | undefined
  /** Whether the matched position has debt in an unsupported vault */
  hasUnsupportedDebt: boolean
  /** Whether positions are still loading */
  isLoading: boolean
}

/**
 * Shared hook that fetches Euler positions and checks whether the active card's
 * sub-account has debt in unsupported vaults.
 *
 * Used by CardConfiguration and ConfigureTab where the sub-account comes from
 * the active card rather than a user-selected position.
 *
 * For AddCardWizard and SolverChangeFlow, use `selectedPosition.hasUnsupportedDebt`
 * directly since they already have a position from the picker.
 */
export function useEulerPositionCheck({
  subAccountId,
  isEuler,
}: UseEulerPositionCheckOptions): UseEulerPositionCheckResult {
  const { positions, isLoading } = useAllEulerPositions({ enabled: isEuler })

  const activePosition = useMemo(() => {
    if (subAccountId == null) return undefined
    return positions.find((p) => p.subAccountId === subAccountId)
  }, [positions, subAccountId])

  return {
    activePosition,
    hasUnsupportedDebt: activePosition?.hasUnsupportedDebt ?? false,
    isLoading,
  }
}
